import ReactGA from 'react-ga4';
import { GOOGLE_ANALYTICS_CONFIG } from '../config/g4a/analytics';
import { hasAnalyticsConsent } from './cookieUtils';

// TypeScript declarations for Google Analytics global functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

let isInitialized = false;

// Function to check and clean up existing GA cookies if consent is not given
export const cleanupExistingCookiesIfNoConsent = () => {
  // Check if analytics consent is given
  const hasConsent = hasAnalyticsConsent();

  if (!hasConsent) {
    // If no consent, clean up any existing GA cookies
    deleteGoogleAnalyticsCookies();
    console.log('Cleaned up existing Google Analytics cookies (no consent)');
  }
};

// Initialize Google Consent Mode with default denied state
export const initConsentMode = () => {
  // Initialize gtag if not already done
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    };
  }

  // Clean up any existing GA cookies first (in case they were created before consent mode)
  cleanupExistingCookiesIfNoConsent();

  // Set default consent state (denied by default for GDPR compliance)
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
};

// Update consent state based on user choice
export const updateConsentMode = (analyticsConsent: boolean) => {
  if (!window.gtag) {
    console.warn('Google Consent Mode not initialized');
    return;
  }

  // If consent is being denied, clean up existing cookies first
  if (!analyticsConsent) {
    deleteGoogleAnalyticsCookies();
  }

  window.gtag('consent', 'update', {
    analytics_storage: analyticsConsent ? 'granted' : 'denied',
    ad_storage: analyticsConsent ? 'granted' : 'denied',
    ad_user_data: analyticsConsent ? 'granted' : 'denied',
    ad_personalization: analyticsConsent ? 'granted' : 'denied',
  });

  // If consent is still denied after update, clean up again to be sure
  if (!analyticsConsent) {
    setTimeout(() => {
      deleteGoogleAnalyticsCookies();
    }, 100);
  }
};

// Function to delete Google Analytics cookies (kept for manual cleanup if needed)
export const deleteGoogleAnalyticsCookies = () => {
  // Get the measurement ID and extract the suffix for the _ga_ cookie
  const measurementId = GOOGLE_ANALYTICS_CONFIG.measurementId;
  const gaSuffix = measurementId.replace('G-', '');

  // List of GA cookies to delete
  const gaCookies = [
    '_ga',
    `_ga_${gaSuffix}`,
    '_gid',
    '_gat',
    `_gat_gtag_${measurementId}`,
    `_gtag_GA_${measurementId}`,
    '_gcl_au',
    '_gcl_aw',
    '_gcl_dc',
    '_gcl_gb',
    '_gcl_gf',
    '_gcl_ha',
  ];

  // Get current domain variations
  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');
  const domains = ['', hostname, `.${hostname}`];

  // Add parent domains if subdomain
  if (domainParts.length > 2) {
    const parentDomain = domainParts.slice(-2).join('.');
    domains.push(parentDomain, `.${parentDomain}`);
  }

  // Delete each GA cookie with multiple combinations for thorough removal
  gaCookies.forEach(cookieName => {
    domains.forEach(domain => {
      // Different path and domain combinations
      const domainAttr = domain ? `;domain=${domain}` : '';

      // Try different expiration methods
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainAttr}`;
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainAttr};SameSite=Lax`;
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainAttr};SameSite=Strict`;
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainAttr};SameSite=None;Secure`;

      // Try with max-age
      document.cookie = `${cookieName}=;max-age=0;path=/${domainAttr}`;
      document.cookie = `${cookieName}=;max-age=0;path=/${domainAttr};SameSite=Lax`;
      document.cookie = `${cookieName}=;max-age=0;path=/${domainAttr};SameSite=Strict`;
      document.cookie = `${cookieName}=;max-age=0;path=/${domainAttr};SameSite=None;Secure`;

      // Try without path
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr}`;
      document.cookie = `${cookieName}=;max-age=0${domainAttr}`;
    });
  });

  console.log('Attempted to delete all Google Analytics cookies');
};

export const initGA = () => {
  // Ensure consent mode is initialized first
  if (!window.gtag) {
    initConsentMode();
  }

  // Only initialize Google Analytics if not already done
  if (!isInitialized) {
    try {
      ReactGA.initialize(GOOGLE_ANALYTICS_CONFIG.measurementId, {
        testMode: GOOGLE_ANALYTICS_CONFIG.debugMode,
      });
      isInitialized = true;
      console.log('Google Analytics: Initialized');
    } catch (error) {
      console.error('Google Analytics: Initialization failed', error);
    }
  }
};

export const trackPageView = (path: string) => {
  if (!isInitialized) {
    return;
  }
  try {
    ReactGA.send({ hitType: 'pageview', page: path });
  } catch (error) {
    console.error('Google Analytics: Page view tracking failed', error);
  }
};

export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (!isInitialized) {
    return;
  }
  try {
    ReactGA.event(eventName, parameters);
  } catch (error) {
    console.error('Google Analytics: Event tracking failed', error);
  }
};

export const trackCustomEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
) => {
  if (!isInitialized) {
    return;
  }
  try {
    ReactGA.event({
      action,
      category,
      label,
      value,
    });
  } catch (error) {
    console.error('Google Analytics: Custom event tracking failed', error);
  }
};

// Function to check if GA is ready
export const isAnalyticsReady = (): boolean => {
  return isInitialized;
};

// Reset function for when consent is withdrawn (now uses Consent Mode)
export const resetAnalytics = () => {
  // Update consent to denied
  updateConsentMode(false);
  // Also manually delete cookies as backup
  deleteGoogleAnalyticsCookies();
  console.log('Google Analytics: Reset due to consent withdrawal');
};
